import { Form } from 'antd';
import React, { memo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import BackOfficeDrawer from 'components/drawers/BackOfficeDrawer';
import BackOfficeEnterpriseForm from 'components/forms/BackOfficeEnterpriseForm';
import { INSUFFICIENT_PERMISSIONS_MESSAGE } from 'constants/internal-admin-roles';
import { addBackOfficeEnterprise } from 'store/modules/backOfficeEnterprises/actions';
import clearErrors from 'store/modules/error/actions';
import { useMyUserHasInternalPermission } from 'store/modules/myUser/hooks';
import { appendValuesToQueryString } from 'store/modules/routerUtils/actions';
import { selectRouterQueryStringDrawer } from 'store/modules/routerUtils/selectors';
import { ProtectedResource, ResourceAction } from 'types/action.enum';
import { EnterpriseStatus, Industry } from 'types/models/enterprise';

const actions = ['ADD_BACK_OFFICE_ENTERPRISE'];

function BackOfficeEnterpriseCreateDrawer(): JSX.Element {
  const [form] = Form.useForm();
  const drawer = useSelector(selectRouterQueryStringDrawer);

  const visible = drawer === 'addEnterprise';

  const dispatch = useDispatch();

  useEffect(() => {
    if (visible) {
      dispatch(clearErrors(actions));
      form.resetFields();
    }
  }, [visible, form, dispatch]);

  const handleClose = useCallback(() => {
    dispatch(
      appendValuesToQueryString({
        drawer: undefined
      })
    );
  }, [dispatch]);

  const handleSave = () => {
    form
      .validateFields()
      .then((values) => {
        dispatch(addBackOfficeEnterprise(values, handleClose));
      })
      .catch(() => {
        // Ignoring promise rejection
      });
  };

  const hasPermission = useMyUserHasInternalPermission({
    resource: ProtectedResource.ENTERPRISE,
    action: ResourceAction.ADD
  });
  if (!hasPermission) {
    return (
      <BackOfficeDrawer
        visible={visible}
        backOfficeSection="Create Enterprise"
        actions={[]}
        noFooter
        handleClose={handleClose}
      >
        {INSUFFICIENT_PERMISSIONS_MESSAGE}
      </BackOfficeDrawer>
    );
  }
  return (
    <BackOfficeDrawer
      visible={visible}
      backOfficeSection="Create Enterprise"
      actions={actions}
      handleSave={handleSave}
      handleClose={handleClose}
    >
      <BackOfficeEnterpriseForm
        actions={actions}
        form={form}
        initialValues={{
          industry: Industry.AGRICULTURE,
          status: EnterpriseStatus.ACTIVE,
          useBillingAddressForShipping: true
        }}
      />
    </BackOfficeDrawer>
  );
}

export default memo(BackOfficeEnterpriseCreateDrawer);
